// src/components/sections/Certification/CertificationCard.jsx
import React, { useState } from "react";
import "./styles/CertificationCard.css";
import { verificationAPI } from "../../../services/verificationAPI";

const CertificationCard = ({
  certification,
  onEdit,
  onDelete,
  isSelected,
  onSelect,
}) => {
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(
    certification.verification?.status
  );
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [resendOpen, setResendOpen] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  };

  const getDateRange = () => {
    const issuedDate = formatDate(certification.issuedAt);
    if (certification.isPursuing) {
      return `Pursuing${issuedDate ? ` (started ${issuedDate})` : ""}`;
    }
    if (!certification.expiryDate) {
      return issuedDate ? `Issued ${issuedDate}` : "";
    }
    const expiryDate = formatDate(certification.expiryDate);
    return `${issuedDate} - ${expiryDate}`;
  };

  const getVerificationStatus = () => {
    const status =
      verificationStatus || certification.verificationStatus || "NOT_REQUESTED";

    const statusConfig = {
      PENDING: {
        label: "Verification Pending",
        class: "pending",
        icon: "⏳",
      },
      IN_PROGRESS: {
        label: "In Progress",
        class: "in-progress",
        icon: "🔄",
      },
      VALIDATED: {
        label: "Verified",
        class: "verified",
        icon: "✅",
      },
      REJECTED: {
        label: "Verification Rejected",
        class: "rejected",
        icon: "❌",
      },
      NOT_REQUESTED: {
        label: "Not Requested",
        class: "not-requested",
        icon: "⚪",
      },
    };

    return statusConfig[status] || statusConfig.NOT_REQUESTED;
  };

  const verificationStatusData = getVerificationStatus();

  const isExpired = () => {
    if (!certification.expiryDate || certification.isPursuing) return false;
    return new Date(certification.expiryDate) < new Date();
  };

  // open resend confirm modal (same UX as EducationCard)
  const handleRequestVerificationClick = () => {
    if (!certification.verifierEmail) {
      alert(
        "Please add a verifier email address before requesting verification."
      );
      return;
    }
    setResendOpen(true);
  };

  const closeResendConfirm = () => setResendOpen(false);

  const confirmResend = async () => {
    setResendOpen(false);
    setVerificationLoading(true);
    setError("");
    setSuccess("");

    try {
      await verificationAPI.requestCertificationVerification(certification.id);
      setVerificationStatus("PENDING");
      const message =
        verificationStatusData.class === "not-requested"
          ? "Certification verification request sent successfully!"
          : "Certification verification request resent successfully!";
      setSuccess(message);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to request verification"
      );
    } finally {
      setVerificationLoading(false);
    }
  };

  return (
    <div
      className={`certification-card ${isSelected ? "selected" : ""} ${
        isExpired() ? "expired" : ""
      }`}
    >
      {/* TOP ROW: main info + actions */}
      <div className="certification-row">
        <div className="certification-main">
          <p className="certification-name">{certification.name}</p>
          <p className="certification-issuer">{certification.issuer}</p>

          {/* extra details like license id or description */}
          {certification.licenseId && (
            <p className="certification-details">
              <span className="certification-field">
                License ID: {certification.licenseId}
              </span>
            </p>
          )}
          {certification.description && (
            <p className="certification-field">
              {certification.description}
            </p>
          )}

          {/* verification badge under issuer */}
          <div className="verification-status">
            <span
              className={`verification-badge ${verificationStatusData.class}`}
            >
              <span className="verification-icon">
                {verificationStatusData.icon}
              </span>
              <span className="verification-label">
                {verificationStatusData.label}
              </span>
            </span>
          </div>

          {/* verifier info like education card */}
          {certification.verifierEmail && (
            <div className="verifier-info">
              <div>
                <strong>Verifier:</strong>
                <strong>{certification.verifierName}</strong>
              </div>
              {certification.verifierEmail && (
                <div className="verifier-email">
                  {certification.verifierEmail}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="certification-actions">
         
         <button
  onClick={onEdit}
  className="btn-icon btn-edit"
  title="Edit Certification"
>
  <svg xmlns="www.w3.org" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
</button>

<button
  onClick={onDelete}
  className="btn-icon btn-delete"
  title="Delete Certification"
>
  <svg xmlns="www.w3.org" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
</button>

        </div>
      </div>

      {/* BOTTOM ROW: dates + resend button */}
      <div className="certification-footer">
        <div className="certification-dates">{getDateRange()}</div>
        <button
          type="button"
          className="resend-btn"
          onClick={handleRequestVerificationClick}
          disabled={verificationLoading}
        >
          {verificationLoading
            ? "Sending..."
            : verificationStatusData.class === "not-requested"
            ? "Request Verification"
            : "Resend Request"}
        </button>
      </div>

      {/* messages under card */}
      {success && <div className="message success-message">{success}</div>}
      {error && <div className="message error-message">{error}</div>}

      {/* resend confirmation dialog (local, same pattern as EducationCard) */}
      {resendOpen && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h3 className="confirm-title">Send verification request</h3>
            <p className="confirm-body">
              This will send a verification request to{" "}
              {certification.verifierEmail}.
            </p>
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-btn confirm-cancel"
                onClick={closeResendConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-btn confirm-send"
                onClick={confirmResend}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationCard;
